import { Button, Space, Tag, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { LeaveRequest, LeaveRequestStatus } from 'types/leave-request';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';
import { colors } from 'theme/tokens';
import { ReviewAction } from './review-leave-request-modal';
import {
  LEAVE_STATUS_COLOR,
  LEAVE_STATUS_LABEL,
  formatLeaveDays,
  getPersonName,
} from '../helpers/leave-request-format';

interface Params {
  canReview: boolean;
  onDetail: (id: number) => void;
  onReview: (request: LeaveRequest, action: ReviewAction) => void;
}

const NO_WRAP = { whiteSpace: 'nowrap' as const };

/** Tổng độ rộng các cột — dùng làm scroll.x (vừa khung bảng 1118px ở màn 1440). */
export const LEAVE_REQUEST_TABLE_WIDTH = 1112;

// "26/09/2026 - 27/09/2026" / "05/09/2026 + 2 ngày": hẹp thì xuống dòng giữa hai cụm,
// không bẻ giữa một ngày.
function renderLeaveDays(text: string) {
  const range = text.split(' - ');
  if (range.length === 2) {
    return (
      <span>
        <span style={NO_WRAP}>{range[0]} -</span> <span style={NO_WRAP}>{range[1]}</span>
      </span>
    );
  }
  const plus = text.indexOf(' + ');
  if (plus > 0) {
    return (
      <span>
        <span style={NO_WRAP}>{text.slice(0, plus)}</span>{' '}
        <span style={NO_WRAP}>{text.slice(plus + 1)}</span>
      </span>
    );
  }
  return <span style={NO_WRAP}>{text}</span>;
}

export function getLeaveRequestColumns({
  canReview,
  onDetail,
  onReview,
}: Params): ColumnsType<LeaveRequest> {
  // Bảng có scroll.x + cột cố định → table-layout: fixed: cột nào cũng cần width (tổng =
  // LEAVE_REQUEST_TABLE_WIDTH). "Trạng thái" đứng ngay sau "Ngày nghỉ" để người duyệt thấy
  // trạng thái mà không phải cuộn ngang ở màn 1280.
  return [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Nhân viên',
      key: 'user',
      width: 160,
      // Phòng ban xuống dòng phụ thay vì "(Kỹ" | "thuật)" bị bẻ giữa cặp ngoặc.
      render: (record) => {
        const department = record.user?.department
          ? DEPARTMENT_LABEL[record.user.department] || record.user.department
          : '';
        return (
          <Tooltip title={record.user?.email || record.user?.username || ''}>
            <div style={{ minWidth: 0 }}>
              <div>{getPersonName(record.user)}</div>
              {department && (
                <div
                  style={{
                    color: colors.textSecondary,
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {department}
                </div>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      // Lý do nằm dòng phụ dưới loại nghỉ (cắt "…", rê chuột xem đủ; đủ trong "Chi tiết") —
      // gộp để cả bảng vừa khung ở màn 1440 mà thẻ loại nghỉ không bị ép xuống 3 dòng.
      title: 'Loại nghỉ / Lý do',
      key: 'leaveType',
      width: 210,
      render: (record) => (
        <div style={{ minWidth: 0 }}>
          <Tag style={{ maxWidth: '100%', whiteSpace: 'normal', marginInlineEnd: 0 }}>
            {record.leaveType?.name || '—'}
          </Tag>
          {record.reason && (
            <Tooltip placement="topLeft" title={record.reason}>
              <div
                style={{
                  marginTop: 4,
                  color: colors.textSecondary,
                  fontSize: 12,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {record.reason}
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Ngày nghỉ',
      key: 'days',
      width: 140,
      render: (record) => renderLeaveDays(formatLeaveDays(record.days)),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 104,
      render: (value: LeaveRequestStatus) => (
        <Tag color={LEAVE_STATUS_COLOR[value]} style={{ borderRadius: 6, padding: '2px 8px' }}>
          {LEAVE_STATUS_LABEL[value] || value}
        </Tag>
      ),
    },
    {
      title: 'Người duyệt',
      key: 'assignedApprover',
      width: 130,
      render: (record) => <span>{getPersonName(record.assignedApprover || undefined)}</span>,
    },
    {
      title: 'Tạo lúc',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      // Cùng định dạng DD/MM/YYYY với cột "Ngày nghỉ".
      render: (value) => (
        <span style={NO_WRAP}>{value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—'}</span>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      // Đủ cho 3 nút icon 36px (đơn chờ duyệt) + khoảng cách 4 + đệm ô 2×16.
      width: 148,
      render: (_: any, record) => (
        <Space size={4}>
          <Tooltip title="Chi tiết">
            <Button type="text" icon={<EyeOutlined />} onClick={() => onDetail(record.id)} />
          </Tooltip>
          {canReview && record.status === LeaveRequestStatus.PENDING && (
            <>
              <Tooltip title="Duyệt">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  onClick={() => onReview(record, 'approve')}
                />
              </Tooltip>
              <Tooltip title="Từ chối">
                <Button
                  danger
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => onReview(record, 'reject')}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];
}
